import {gql} from '@apollo/client';

import {assert, assertAndContinue} from 'Asserts';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  FetchContainersFromPlateWellQuery,
  FetchContainersFromPlateWellQueryVariables,
} from 'notebook/structured-tables/util/__generated/FetchContainersFromPlateWellQuery';
import {
  FetchContainersPlateSizeQuery,
  FetchContainersPlateSizeQueryVariables,
} from 'notebook/structured-tables/util/__generated/FetchContainersPlateSizeQuery';
import {SampleLookupInfoForCreateRequest} from 'requests/creation/CreateRequestFromSamplesButton';
import {InvalidPositionError, parseGridPosition, Position} from 'samples/SampleUtil';

type PlateNode = ExtractByTypename<FetchContainersFromPlateWellQuery['directStorable'], 'PlateNode'>;

type PlateSizePlateNode = ExtractByTypename<FetchContainersPlateSizeQuery['directStorable'], 'PlateNode'>;

type PlateSchema = PlateSizePlateNode['storableSchema'];

export const FETCH_CONTAINERS_FROM_PLATE_WELL_QUERY = gql`
  query FetchContainersFromPlateWellQuery($input: StorableInput!) @skipAudit @engComponent(name: INVENTORY) {
    directStorable(input: $input) {
      ... on PlateNode {
        id
        name
        cell {
          ...GridCellIndexesGraphQL
          container {
            ... on ContainerNode {
              id
              name
              storableSchema {
                id
                iconId
              }
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('GridCellIndexes').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const FETCH_CONTAINERS_PLATE_SIZE_QUERY = gql`
  query FetchContainersPlateSizeQuery($input: StorableInput!) @skipAudit {
    directStorable(input: $input) {
      ... on PlateNode {
        id
        storableSchema {
          id
          height
          width
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function getPlateWellCoordinates(plateSchema: PlateSchema, plateWellPosition: string): Position {
  let rowIndex;
  let columnIndex;
  try {
    const {height: plateHeight, width: plateWidth} = plateSchema;
    ({rowIndex, columnIndex} = parseGridPosition({
      position: plateWellPosition,
      gridHeight: plateHeight,
      gridWidth: plateWidth,
    }));
  } catch (e) {
    if (e instanceof InvalidPositionError) {
      throw new Error('Expected valid position');
    } else {
      throw e;
    }
  }
  return {rowIndex, columnIndex};
}

export interface FetchedContainerInfo {
  schemaId: string | null;
  containers: Array<{id: string; name: string; iconId: string}>;
}

export default async function fetchContainersFromPlateWellPositions(
  plateSampleInfos: Array<SampleLookupInfoForCreateRequest>
): Promise<FetchedContainerInfo | null> {
  let schemaId = null;
  const containers = [];
  for (const plateSampleInfo of plateSampleInfos) {
    assert(plateSampleInfo.plateWellPositionString != null, 'Expected a plate well position');

    const plateSizeResponse = await doQuerySafe<
      FetchContainersPlateSizeQuery,
      FetchContainersPlateSizeQueryVariables
    >({query: FETCH_CONTAINERS_PLATE_SIZE_QUERY}, {variables: {input: {id: plateSampleInfo.idForLookup}}});
    const plate = plateSizeResponse.directStorable as PlateSizePlateNode;
    if (plate == null || isInaccessible(plate)) {
      return null;
    }
    const {rowIndex, columnIndex} = getPlateWellCoordinates(
      plate.storableSchema,
      plateSampleInfo.plateWellPositionString
    );
    const plateWellResponse = await doQuerySafe<
      FetchContainersFromPlateWellQuery,
      FetchContainersFromPlateWellQueryVariables
    >(
      {query: FETCH_CONTAINERS_FROM_PLATE_WELL_QUERY},
      {
        variables: {
          input: {
            id: plateSampleInfo.idForLookup,
            rowIndex,
            colIndex: columnIndex,
          },
        },
        fetchPolicy: 'network-only',
      }
    );
    if (isInaccessible(plateWellResponse.directStorable)) {
      return null;
    }
    const cell = (plateWellResponse.directStorable as PlateNode).cell;
    assert(cell != null, 'Plate well query should have returned a plate and cell');
    const {container} = cell;
    if (isInaccessible(container)) {
      continue;
    }

    // We don't expect this function to ever get called with plate well samples from different plate schemas,
    // so we assume all the plate wells are of the same schema.
    schemaId = schemaId ?? container.storableSchema.id;
    assertAndContinue(
      schemaId === container.storableSchema.id,
      'While creating a workflow from a structured table, plate well samples in the same column were found ' +
        'with different container schema ids.'
    );
    containers.push({
      id: container.id,
      name: container.name,
      iconId: container.storableSchema.iconId,
    });
  }
  return {schemaId, containers};
}
